import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, XCircle, Mail } from "lucide-react";
import { api } from "@/lib/api";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("polling");

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let attempts = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState("paid");
          return;
        }
      } catch (e) {
        // keep polling briefly
      }
      attempts += 1;
      if (attempts < 20) timer = setTimeout(poll, 2000);
      else setState("error");
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <main className="min-h-screen bg-charcoal text-parchment flex items-center justify-center px-6 grain relative" data-testid="payment-success-page">
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
        className="glass rounded-3xl p-10 max-w-md w-full text-center">
        {state === "polling" && (
          <>
            <Loader2 className="w-12 h-12 text-terracotta animate-spin mx-auto" data-testid="success-loading-icon" />
            <h1 className="mt-6 font-display font-bold text-2xl">Confirming your payment…</h1>
            <p className="mt-3 text-sm text-mist">Hold tight while Stripe confirms.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="w-14 h-14 text-fern mx-auto" data-testid="success-check-icon" />
            <h1 className="mt-6 font-display font-bold text-3xl" data-testid="success-headline">You're on the route.</h1>
            <p className="mt-4 text-sm text-mist leading-relaxed">
              Your valet trash service is active. A confirmation email with your unit number,
              plan, and pickup window is on its way to your inbox.
            </p>
            <div className="mt-6 flex items-center justify-center gap-2 text-dim">
              <Mail className="w-4 h-4" />
              <span className="font-mono text-[10px] uppercase tracking-[0.2em]">Confirmation email sent</span>
            </div>
          </>
        )}
        {state === "error" && (
          <>
            <XCircle className="w-12 h-12 text-terracotta mx-auto" data-testid="success-error-icon" />
            <h1 className="mt-6 font-display font-bold text-2xl">We couldn't confirm yet</h1>
            <p className="mt-3 text-sm text-mist">If your card was charged, your subscription is active — check your email for confirmation.</p>
          </>
        )}
        <Link to="/" data-testid="success-home-link"
          className="mt-8 inline-block bg-terracotta hover:bg-parchment hover:text-charcoal text-parchment text-sm font-semibold px-8 py-3.5 rounded-full transition-colors duration-300">
          Back to Home
        </Link>
      </motion.div>
    </main>
  );
}
